import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bookmark, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { scrollToSection } from "@/hooks/useLenis";

const LINKS = [
  { label: "Home", id: "#home" },
  { label: "Routes", id: "#routes" },
  { label: "About", id: "#about" },
];

const ease = [0.22, 1, 0.36, 1];

export default function Navbar({ journalCount = 0, onOpenJournal }) {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-forest-600/40 bg-forest-950/80 backdrop-blur-xl">
      <nav className="flex h-16 items-center justify-between px-5 md:h-[72px] md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => go("#home")}
          className="flex items-center gap-2.5"
        >
          <Logo className="h-9 w-9" />
          <span className="font-display text-xl tracking-tight text-cream">EcoRoute</span>
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              onClick={() => go(l.id)}
              className="nav-link text-sm text-forest-dim transition-colors hover:text-cream"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="journal-button"
            onClick={() => onOpenJournal?.()}
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-forest-600/50 text-cream transition-colors hover:border-moss/70 hover:text-moss"
            aria-label="Open trail journal"
          >
            <Bookmark className="h-4 w-4" />
            {journalCount > 0 && (
              <span
                data-testid="journal-count"
                className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-moss px-1 font-mono text-[9px] font-semibold text-forest-950"
              >
                {journalCount}
              </span>
            )}
          </button>
          <span
            data-testid="touch-grass-badge"
            className="hidden items-center gap-2 rounded-full border border-forest-500/50 bg-forest-700/60 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-moss sm:flex"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-moss opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-moss" />
            </span>
            Touch Grass
          </span>
          <button
            data-testid="nav-menu-button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-forest-600/50 text-cream md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease }}
            className="overflow-hidden border-t border-forest-600/40 bg-forest-950/95 md:hidden"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                  onClick={() => go(l.id)}
                  className="border-b border-forest-600/30 py-3.5 text-left font-display text-xl text-cream last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <span className="mt-3 flex w-fit items-center gap-2 rounded-full border border-forest-500/50 bg-forest-700/60 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-moss">
                <span className="h-2 w-2 rounded-full bg-moss" />
                Touch Grass
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
